// Shared pieces of the three elevation data_json DTOs. See the full field
// classification and the sync/gate rules in
// pages/project/shopdrawing/elevationTool/data-json-dtos.md.

// type-only: the glass vocabulary is owned by the model. Kept type-only so this wire
// layer has no runtime dependency on pages/ (the model imports these DTOs back).
import type {MdGlassBead, MdGlassType} from "../../pages/project/shopdrawing/elevationTool/model/glass/MdGlass";

// bump on any breaking change to a *ElevationData shape.
export const ELEVATION_DTO_VERSION = 1;

// a serialized FrmVar holding a distance: its value in inches (frmVar.get()/inches),
// decoupled from the drawing-unit `inches` scale constant. `mod` mirrors
// FrmVar.isModified() and is load-bearing (it decides whether a height/width change
// re-derives the var — see the hinge auto path and the lite solver); omitted when false.
// Calculated FrmVars are never serialized.
export type SerFrmVar = {
    in: number;
    mod?: boolean;
};

// a serialized FrmVar whose value is a plain scalar (a count/ratio), NOT a distance,
// so it is not inches-scaled. Used for the hinge count.
export type SerScalarVar = {
    value: number;
    mod?: boolean;
};

// the five Synced identity props — stored on both the elevation and the opening,
// kept in lock-step, and gated on exact equality when applying an elevation to an
// opening. Editing any of these in the tool writes the opening's matching columns in
// the same transaction (see data-json-dtos.md "Identity").
export type ElevationIdentity = {
    openingType: string;              // raw string; isPair = === "Pair"
    frameProfile: string;
    handing: string;
    flipDoubleActiveHanding: boolean;
    locationTransition: "to" | "from" | "";
};

// snapshot of the per-opening dimensions the design was built against. NOT re-applied
// from here (fromOpening re-applies them from the live opening) — kept only to detect
// drift at load and flag for review.
export type DimensionFingerprint = {
    nominalWidth: string;
    inactiveDoorWidth: string;
    nominalHeight: string;
    jambDepth: string;
    doorThickness: string;
};

// serialized MdGlass — how a lite is glazed. Every field is omitted when it is on the
// model default, so an older blob (no `glass` at all) reads as the defaults. The two
// sizes are in inches, like SerFrmVar.in, so the blob stays independent of the
// drawing-unit `inches` constant.
export type SerGlass = {
    thermal?: boolean;
    type?: MdGlassType;
    thicknessIn?: number;
    bead?: MdGlassBead;
    undersizeIn?: number;
};
